import React, { useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { PlayerMatchupTypes, PlayerMatchupFormats, PitchingDecisionTypes } from '@mlb/react-patterns/v4/components/PlayerMatchup/playerMatchup.types';
import {
    PlayerListWrapper,
    PlayerListTitle,
    PlayerListItemWrapper,
    DueUpPlayerListTitle,
    DueUpOrderNum,
    DueUpPlayerListItemWrapper,
    InfoDivider,
    DueUpDivider
} from './PlayerList.style';
import TeamTypes from '../Boxscore/boxscore/boxscoreTeam/config/TeamTypes';
import { statsMapsDuel } from '../PlayerDuel/utils/statsMapsDuel';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import buildPlayerStatsDuel from '../PlayerDuel/utils/buildPlayerStatsDuel';
import { StatsDividerWrapper } from '../PlayerDuel/PlayerDuel.style';
import GDBREAKPOINTS from '@gameday/components/themes/breakpoints';
import { useMedia } from '@mlb/react-patterns/v4/utils/hooks/useMedia/useMedia';
import { liveViewsTypes } from '../themes/liveViewsTypes';
import useTracking from '../../utils/hooks/useTracking';
import PlayerMatchupWrapper from '../PlayerDuel/components/PlayerMatchupWrapper';

const headshotWidth = '48px';
const tempDecisionLabelMap = {
    W: 'Win',
    L: 'Loss',
    S: 'Save'
};

const { FULLNARROW, SINGLENARROW } = liveViewsTypes;

const PlayerList = ({ data = {}, dueUpNumber = 3, hideOrder, hideBatSide, language, deviceInteraction }) => {
    const {
        liveData = {},
        gameData: {
            status: {
                abstractGameCode
            } = {}
        } = {}
    } = data;

    let list = [];
    let listTitle = '';
    let ListTitleComp = PlayerListTitle;
    let listType = '';
    let showDivider = false;

    const translate = useTranslate();
    const dictMap = {
        avg: translate('labelBrief_h_avg', 'avg'),
        hr: translate('labelBrief_h_hr', 'hr'),
        bb: translate('labelBrief_h_bb', 'bb'),
        slg: translate('labelBrief_h_slg', 'slg'),
        ip: translate('labelBrief_ip', 'ip'),
        p: translate('labelBrief_p', 'p'),
        era: translate('labelBrief_era', 'era'),
        bb: translate('labelBrief_p_bb', 'bb'),
        pitcherK: translate('labelBrief_p_k', 'k'),
        whip: translate('labelBrief_p_whip', 'whip'),
        saves: translate('save_abbrev', 'sv'),
        blownSaves: translate('labelBrief_bs', 'bs'),
        holds: translate('labelBrief_hld', 'hld'),
        strikeOuts: translate('labelBrief_h_k', 'k'),
        er: translate('labelBrief_er', 'er'),
        '3b': translate('labelBrief_t', '3b'),
        '2b': translate('labelBrief_d', '2b'),
        cs: translate('labelBrief_h_cs', 'cs'),
        runs: translate('labelBrief_h_r', 'r'),
        sb: translate('labelBrief_h_sb', 'sb'),
        hbp: translate('labelBrief_hbp', 'hbp'),
        rbi: translate('labelBrief_rbi', 'rbi'),
        wins: translate('win_abbrev:', 'w'),
        losses: translate('loss_abbrev:', 'l')
    };

    let viewType;
    const isMobile = useMedia({ maxWidth: `${GDBREAKPOINTS.XS_MAX}` });
    const isFullNarrow = useMedia({ maxWidth: `${GDBREAKPOINTS.LG_MAX}`, minWidth: `${GDBREAKPOINTS.LG_MIN}` });
    const Tracking = useTracking();
    const onClick = useCallback((e) => {
        const text = e?.target.innerText;
        const playerId = e?.currentTarget?.dataset?.playerid;
        const url = e?.currentTarget?.dataset?.url;

        const activity = 'Player Click';
        const cfg = {
            componentName: ['Live', 'Due Up'],
            text,
            activity,
            linkName: `${activity}: ${playerId}`
        };
        Tracking.track.track_click(cfg);

        if (deviceInteraction.playercard({ title: 'Player Card', url, pid: playerId })) {
            e.preventDefault();
        }
    }, [Tracking, deviceInteraction, language]);

    if (isMobile) {
        viewType = SINGLENARROW;
    } else if (isFullNarrow) {
        viewType = FULLNARROW;
    };

    // if Live game state
    if (abstractGameCode === 'L') {
        const {
            boxscore: {
                teams = {}
            } = {},
            linescore: {
                offense: {
                    battingOrder: orderOffset
                } = {},
                inningState,
                currentInning,
                inningHalf
            } = {}
        } = liveData;

        const isTop = inningHalf === 'Top';
        const targetInning = isTop ? currentInning : currentInning + 1;
        const inningTranslated = translate(`ranking_${targetInning}`, targetInning);
        const replacement = { inning: targetInning, inningOrdinal: inningTranslated };
        const DueupTranslated = translate('due_up', 'Due Up');
        const title = translate(isTop ? 'inning_state_bottom' : 'inning_state_top', `${isTop ? 'Bottom' : 'Top'} ${targetInning}`, replacement);

        // TODO: translate
        listTitle = `${DueupTranslated} ${(title) ? ` - ${title}` : ''}`;
        ListTitleComp = DueUpPlayerListTitle;
        listType = 'dueup';
        showDivider = listType === 'dueup';

        // TODO eventually want to let contentful user configure which stats to render for player matchups
        // in the mean time static choices selected here in desiredStats
        // TODO need to figure out getting ab results onto the player object here -- would need to update gumbo model
        const desiredHittingStats = {
            before: ['hits'],
            after: ['homeRuns', 'triples', 'doubles', 'baseOnBalls', 'hitByPitch', 'strikeOuts', 'rbi', 'runs', 'stolenBases', 'caughtStealing']
        };
        // need to generate upcoming n players so will need to loop through a selected team's batting order
        const upcomingBattingOrder = teams[inningState === 'Middle' ? TeamTypes.HOME : TeamTypes.AWAY]?.battingOrder;
        const battingOrderLength = upcomingBattingOrder.length;
        const requiresBreak = !isMobile && !isFullNarrow;
        for (let i = 0; i < dueUpNumber; i++) {
            const pointer = (i + (orderOffset - 1)) % battingOrderLength;
            const {
                jerseyNumber,
                person: {
                    id,
                    boxscoreName,
                    fullName,
                    batSide: { code: batSideCode } = {},
                    nameSlug
                } = {},
                stats
            } = upcomingBattingOrder[pointer];
            const playerStats = buildPlayerStatsDuel(desiredHittingStats, statsMapsDuel.hittersStatsMap, stats, dictMap, false, requiresBreak);

            let jerseyAndBatSide = null;
            if (!hideBatSide) {
                jerseyAndBatSide = <>{ (isMobile || isFullNarrow) && `#${jerseyNumber}`}<StatsDividerWrapper>|</StatsDividerWrapper>{batSideCode}</>;
            }
            list.push(
                <Fragment key={id}>
                    <DueUpPlayerListItemWrapper key={ id } $type={viewType}>
                        { !hideOrder && (isMobile || isFullNarrow) && <DueUpOrderNum $type={viewType}>{ pointer + 1 }</DueUpOrderNum> }
                        <PlayerMatchupWrapper
                            showHeadShot
                            playerId={ id }
                            name={ isMobile || isFullNarrow ? fullName : boxscoreName }
                            type={ PlayerMatchupTypes.HITTERLIVE }
                            format={ PlayerMatchupFormats.LIST }
                            customPlayerInfo={ jerseyAndBatSide } // TODO translate
                            playerStats={ playerStats }
                            headshotWidth={ headshotWidth }
                            language={ language }
                            linkProps={{
                                handlers: { onClick }
                            }}
                            headshotZoomScale={1.0}
                            $type={SINGLENARROW}
                        />
                    </DueUpPlayerListItemWrapper>
                    {/* show a <DueUpDivider /> after every batter except the last, and not on mobile */}
                    {i !== dueUpNumber - 1 && !isMobile && !isFullNarrow && <DueUpDivider />}
                </Fragment>
            );
        }

    // if Final game state
    } else if (abstractGameCode === 'F') {
        const {
            decisions: {
                winner,
                loser,
                save
            } = {}
        } = liveData;

        const decisionsArr = [winner, loser];
        if (save) {
            decisionsArr.push(save);
        }

        // TODO eventually want to let contenful user configure which stats to render for player matchups
        // in the mean time static choices selected here in desiredStats
        const desiredPitchingStats = {
            before: ['wins', 'losses', 'saves', 'blownSaves', 'holds'],
            after: ['ip', 'pitcherK', 'earnedRuns']
        };

        list = decisionsArr.map(({
            person: {
                id,
                boxscoreName,
                nameSlug
            } = {},
            stats
        }, decisionsIndex) => {
            const playerStats = buildPlayerStatsDuel(desiredPitchingStats, statsMapsDuel.pitchersStatsMap, stats, dictMap, true);

            // TODO translate
            const decision = decisionsIndex === 0
                ? tempDecisionLabelMap[PitchingDecisionTypes.W]
                : decisionsIndex === 1
                    ? tempDecisionLabelMap[PitchingDecisionTypes.L]
                    : tempDecisionLabelMap[PitchingDecisionTypes.S];

            const onClick = useCallback((e) => {
                const text = e?.target.innerText;
                const playerId = e?.currentTarget?.dataset?.playerid;
                const url = e?.currentTarget?.dataset?.url;

                const cfg = {
                    componentName: ['Live', 'Match up'],
                    text,
                    activity: 'Link Click'
                };
                Tracking.track.track_click(cfg);

                if (!deviceInteraction.playercard({ title: 'Player Card', url, pid: playerId })) {
                    e.preventDefault();
                }
            }, [Tracking, deviceInteraction, language]);

            return (
                <PlayerListItemWrapper key={ id }>
                    <PlayerMatchupWrapper
                        playerId={ id }
                        name={ boxscoreName }
                        type={ PlayerMatchupTypes.PITCHERPOSTGAME }
                        format={ PlayerMatchupFormats.LIST }
                        playerStats={ playerStats }
                        headshotWidth={ headshotWidth }
                        language={ language }
                        linkProps={{
                            handlers: { onClick }
                        }}
                        decision={decision}
                        $type={SINGLENARROW}
                    />
                </PlayerListItemWrapper>
            );
        });
    }

    return (
        <Fragment key={listType}>
            <PlayerListWrapper className={listType} $type={viewType}>
                { listTitle && (isMobile || isFullNarrow) && <ListTitleComp $type={viewType}>{ listTitle }</ListTitleComp> }
                { list }
            </PlayerListWrapper>
            {showDivider && !isFullNarrow && <InfoDivider />}
            { listTitle && !isMobile && !isFullNarrow && <ListTitleComp>{listTitle}</ListTitleComp>}
        </Fragment>
    );
};

PlayerList.propTypes = {
    data: PropTypes.object,
    dueUpNumber: PropTypes.number, // TODO wire up dueUpNumber with cms when ready
    hideOrder: PropTypes.bool,
    hideBatSide: PropTypes.bool,
    language: PropTypes.string
};

export default PlayerList;
